<template>
  <button
    type="button"
    :class="buttonClasses"
    :disabled="disabled || loading"
    v-on="$listeners"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @focus="onFocus"
    @blur="onBlur"
  >
    <v-progress-circular
      v-if="loading"
      indeterminate
      size="18"
      width="2"
      :color="iconColor"
    />
    <template v-else>
      <SvgIcon v-if="svgIconName" :name="svgIconName" :color="iconColor" />
      <slot />
      <SvgIcon
        v-if="svgIconNameRight"
        :name="svgIconNameRight"
        :color="iconColor"
      />
    </template>
  </button>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";

@Component
export default class BaseButton extends Vue {
  @Prop({
    default: "primary",
    validator: (value: string) =>
      [
        "primary",
        "secondary",
        "tertiary",
        "cancel",
        "remove",
        "radio",
        "publish",
        "reaction",
        "filter",
        "options",
        "error",
        "chevron",
        "dateTime",
        "club",
        "reactionModal",
      ].includes(value),
  })
  typeButton!: string;

  @Prop({ default: false }) disabled!: boolean;

  @Prop({ type: Boolean, default: false }) loading!: boolean;

  @Prop({ default: false }) selected!: boolean;

  @Prop({ default: "" }) svgIconName!: string;

  @Prop({ default: "" }) svgIconNameRight!: string;

  @Prop({ type: Boolean, default: false }) isDarkContext!: boolean;

  @Prop({ type: Boolean, default: false }) expandOnSelect!: boolean;

  isHovered = false;
  isPressed = false;
  isFocusVisible = false;

  get isXs(): boolean {
    return this.$vuetify.breakpoint.xs;
  }

  get isIconOnly(): boolean {
    const hasIcon = !!this.svgIconName || !!this.svgIconNameRight;
    const slots = this.$slots.default;
    const hasSlotContent = slots
      ? slots.some(
          (vnode) => !!vnode.tag || (!!vnode.text && !!vnode.text.trim())
        )
      : false;
    return hasIcon && !hasSlotContent;
  }

  get buttonClasses(): string[] {
    const classes = ["new-base-button", `new-base-button--${this.typeButton}`];

    if (this.typeButton !== "publish" && this.typeButton !== "filter") {
      classes.push(
        this.isXs ? "new-base-button--xs" : "new-base-button--sm-and-up"
      );
    }

    if (
      this.typeButton === "radio" ||
      this.typeButton === "filter" ||
      this.typeButton === "tertiary" ||
      this.typeButton === "secondary" ||
      this.typeButton === "options" ||
      this.typeButton === "club" ||
      this.typeButton === "reactionModal"
    ) {
      classes.push(
        this.selected
          ? "new-base-button--selected"
          : "new-base-button--unselected"
      );
    }

    if (this.isDarkContext) {
      classes.push("new-base-button--dark-context");
    }

    if (this.isIconOnly) {
      classes.push("new-base-button--icon-only");
    }

    if (this.expandOnSelect) {
      classes.push("new-base-button--expand-on-select");
    }

    return classes;
  }

  get iconColor(): string {
    if (this.typeButton === "reaction") {
      if (this.disabled) return "gray50";
      if (this.isPressed) return "purpleShadow";
      if (this.isHovered) return "purple100";
      if (this.isFocusVisible) return "gray70";
      if (this.isDarkContext) return "white";
      return "gray70";
    }

    if (this.disabled) {
      return this.typeButton === "remove" ? "redMuted" : "gray50";
    }

    switch (this.typeButton) {
      case "primary":
      case "cancel":
      case "publish":
        return "white";
      case "secondary":
        if (this.selected) return "white";
        return this.isHovered || this.isPressed ? "purpleHover" : "purple100";
      case "club":
        if (this.selected) {
          return "purple100";
        }
        return "white";
      case "reactionModal":
      case "tertiary":
        if (this.isDarkContext) return "white";
        if (this.selected) {
          return "purple100";
        }
        return "purple100";
      case "remove":
        return this.isHovered || this.isPressed ? "white" : "redBright";
      case "filter":
        if (this.selected) {
          return this.isHovered || this.isPressed ? "purpleBrand" : "white";
        }
        return "purpleBrand";
      case "radio":
        return this.selected ? "purpleBrand" : "black";
      case "options":
        if (this.selected) {
          return "purpleBrand";
        }
        return this.isHovered || this.isFocusVisible ? "purpleBrand" : "black";
      default:
        return "purple100";
    }
  }

  onMouseEnter(): void {
    this.isHovered = true;
  }

  onMouseLeave(): void {
    this.isHovered = false;
    this.isPressed = false;
  }

  onMouseDown(): void {
    this.isPressed = true;
  }

  onMouseUp(): void {
    this.isPressed = false;
  }

  onFocus(): void {
    this.isFocusVisible = true;
  }

  onBlur(): void {
    this.isFocusVisible = false;
  }
}
</script>

<style lang="sass" scoped>
.new-base-button
  display: inline-flex
  justify-content: center
  align-items: center
  gap: 4px
  border: none
  outline: none
  cursor: pointer
  font-family: "Open Sans", sans-serif
  font-style: normal
  font-weight: 700
  line-height: normal
  text-align: center
  box-sizing: border-box
  appearance: none
  -webkit-appearance: none
  transition: background 0.5s ease, box-shadow 0.5s ease, color 0.5s ease, border-color 0.5s ease

  // ========================
  // SIZE: smAndUp (>=600px)
  // ========================
  &--sm-and-up
    height: 40px
    padding: 16px
    border-radius: 16px
    font-size: 14px

  // ========================
  // SIZE: xs (<600px)
  // ========================
  &--xs
    height: 28px
    padding: 16px 8px
    border-radius: 10px
    font-size: 12px

  // ========================
  // ICON-ONLY: round button
  // ========================
  &--icon-only
    border-radius: 50% !important
    padding: 0 !important
    &.new-base-button--sm-and-up
      width: 40px
      min-width: 40px
    &.new-base-button--xs
      width: 28px
      min-width: 28px

  // ========================
  // TYPE: PRIMARY
  // ========================
  &--primary
    background: var(--v-purple100-base)
    color: var(--v-white-base)
    border: 2px solid transparent

    &:disabled
      background: var(--v-gray35-base)
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      background: var(--v-purpleHover-base)
      box-shadow: 0 1px 0 0 var(--v-black-base), 0 2px 4px 0 var(--v-bluePartner-base)

    &:active:not(:disabled)
      background: var(--v-purpleHover-base)
      box-shadow: 0 3px 4px 0 var(--v-purpleDeep-base) inset

    &:focus-visible:not(:disabled)
      border-color: var(--v-infoBlue-base)
      background: var(--v-purple100-base)

  // ========================
  // TYPE: SECONDARY
  // ========================
  &--secondary
    background: var(--v-white-base)
    color: var(--v-purple100-base)
    border: 1px solid var(--v-purple100-base)

    &:disabled
      border-color: var(--v-gray50-base)
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      border-color: var(--v-purpleHover-base)
      background: var(--v-white-base)
      box-shadow: 0 2px 4px 0 rgba(3, 201, 186, 0.70)
      color: var(--v-purpleHover-base)

    &:active:not(:disabled)
      border-color: var(--v-purpleHover-base)
      background: rgba(255, 255, 255, 0.10)
      box-shadow: 0 2px 4px 0 var(--v-purpleHover-base) inset
      color: var(--v-purple100-base)

    &:focus-visible:not(:disabled)
      border: 2px solid var(--v-purple100-base)
      color: var(--v-purple100-base)

    // --- SECONDARY SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purpleSecondary-base)
      color: var(--v-white-base)
      border-color: var(--v-purpleSecondary-base)

  // ========================
  // TYPE: TERTIARY
  // ========================
  &--tertiary
    background: transparent
    color: var(--v-purple100-base)
    border: 2px solid transparent

    &:disabled
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      background: var(--v-purplePale-base)
      color: var(--v-purple100-base)

    &:active:not(:disabled)
      background: var(--v-purplePale-base)
      box-shadow: 0 3px 4px 0 var(--v-purpleMedium-base) inset
      color: var(--v-purple100-base)

    &:focus-visible:not(:disabled)
      border-color: var(--v-tealFocus-base)
      color: var(--v-purple100-base)

    // --- TERTIARY SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purple40-base)
      color: var(--v-purple100-base)
      line-height: 20px

      &:hover:not(:disabled)
        background: var(--v-purplePale-base)
        color: var(--v-purple100-base)

      &:active:not(:disabled)
        background: var(--v-purplePale-base)
        box-shadow: 0 3px 4px 0 var(--v-purpleMedium-base) inset
        color: var(--v-purple100-base)

      &:focus-visible:not(:disabled)
        border-color: var(--v-tealFocus-base)
        color: var(--v-purple100-base)

    // --- TERTIARY DARK CONTEXT (deve vir depois de selected para ter prioridade) ---
    &.new-base-button--dark-context
      color: var(--v-white-base) !important

      &:hover:not(:disabled)
        background: rgba(255, 255, 255, 0.15)
        color: var(--v-white-base) !important

      &:active:not(:disabled)
        background: rgba(255, 255, 255, 0.15)
        box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.25) inset
        color: var(--v-white-base) !important

      &:focus-visible:not(:disabled)
        border-color: var(--v-tealFocus-base)
        color: var(--v-white-base) !important

  // ========================
  // TYPE: CANCEL
  // ========================
  &--cancel
    background: var(--v-gray70-base)
    color: var(--v-white-base)
    border: 2px solid transparent

    &:disabled
      background: var(--v-gray5-base)
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      background: var(--v-gray90-base)
      box-shadow: 0 2px 4px 0 rgba(3, 201, 186, 0.70)

    &:active:not(:disabled)
      background: var(--v-gray90-base)
      box-shadow: 0 3px 4px 0 var(--v-black-base) inset

    &:focus-visible:not(:disabled)
      border-color: var(--v-tealFocus-base)
      background: var(--v-gray70-base)

  // ========================
  // TYPE: REMOVE
  // ========================
  &--remove
    background: var(--v-redPale-base)
    color: var(--v-redBright-base)
    border: 2px solid transparent

    &:disabled
      background: var(--v-redLight-base)
      color: var(--v-redMuted-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      background: var(--v-redDark-base)
      color: var(--v-white-base)

    &:active:not(:disabled)
      background: var(--v-redDark-base)
      box-shadow: 0 3px 4px 0 var(--v-redDeep-base) inset
      color: var(--v-white-base)

    &:focus-visible:not(:disabled)
      border-color: var(--v-tealFocus-base)
      background: var(--v-redPale-base)
      color: var(--v-redBright-base)

    // --- REMOVE ICON-ONLY (sem background) ---
    &.new-base-button--icon-only
      background: transparent

      &:hover:not(:disabled)
        background: var(--v-redPale-base)

      &:active:not(:disabled)
        background: var(--v-redPale-base)
        box-shadow: 0 3px 4px 0 var(--v-redDeep-base) inset

      &:focus-visible:not(:disabled)
        background: transparent

  // ========================
  // TYPE: ERROR
  // ========================
  &--error
    background: var(--v-white-base)
    color: var(--v-redBright-base)
    border: 1px solid var(--v-redBright-base)


    &:disabled
      background: var(--v-white-base)
      color: var(--v-redMuted-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      background: var(--v-white-base)
      color: var(--v-redBright-base)
      box-shadow: 0 1px 0 0 var(--v-black-base), 0 2px 4px 0 var(--v-bluePartner-base)

    &:active:not(:disabled)
      background: var(--v-white-base)
      box-shadow: 0 3px 4px 0 var(--v-redDeep-base) inset
      color: var(--v-redBright-base)

    &:focus-visible:not(:disabled)
      border-color: var(--v-tealFocus-base)
      background: var(--v-white-base)
      color: var(--v-redBright-base)

  // ========================
  // TYPE: RADIO
  // ========================
  &--radio
    gap: 8px
    border: 1px solid var(--v-grayTransmission-base)
    font-weight: 400
    line-height: 18px
    text-align: left
    transition: background 0.5s ease, box-shadow 0.5s ease, color 0.5s ease, border-color 0.5s ease, text-decoration 0.5s ease
    height: 54px

    &::before
      content: ''
      display: inline-block
      width: 22px
      height: 22px
      min-width: 22px
      border-radius: 50%
      flex-shrink: 0
      transition: background 0.5s ease
      background: var(--v-gray35-base)

    // --- RADIO SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purplePale-base)
      color: var(--v-purpleBrand-base)

      &::before
        background: radial-gradient(circle, var(--v-purpleBrand-base) 3.5px, var(--v-white-base) 3.5px)

      &:disabled
        background: var(--v-gray35-base)
        color: var(--v-purpleSecondary-base)
        cursor: not-allowed
        box-shadow: none

        &::before
          background: radial-gradient(circle, var(--v-purpleSecondary-base) 3.5px, var(--v-white-base) 3.5px)

      &:hover:not(:disabled)
        border-color: var(--v-purpleBrand-base)
        text-decoration: underline

      &:active:not(:disabled)
        box-shadow: 0 2px 4px 0 var(--v-purpleShadow-base) inset

      &:focus-visible:not(:disabled)
        border-color: var(--v-tealFocus-base)

    // --- RADIO UNSELECTED (INATIVO) ---
    &.new-base-button--unselected
      background: transparent
      color: var(--v-black-base)
      border: 1px solid var(--v-grayTransmission-base)

      &:disabled
        color: var(--v-gray70-base)
        cursor: not-allowed
        box-shadow: none

        &::before
          background: var(--v-gray35-base)

      &:hover:not(:disabled)
        border-color: var(--v-purpleBrand-base)
        text-decoration: underline

      &:active:not(:disabled)
        border-color: var(--v-purpleBrand-base)
        background: var(--v-white-base)
        box-shadow: 0 2px 4px 0 var(--v-gray70-base) inset

      &:focus-visible:not(:disabled)
        border-color: var(--v-tealFocus-base)
        background: var(--v-white-base)

    // --- XS SIZE OVERRIDE ---
    &.new-base-button--xs
      padding: 8px 16px
      border-radius: 8px
      height: 38px

  // ========================
  // TYPE: DATETIME
  // ========================
  &--dateTime
    border: 1px solid var(--v-grayTransmission-base)
    background: transparent
    height: 54px !important
    font-style: normal
    font-weight: 400
    line-height: normal

    &:disabled
      background: var(--v-gray35-base)
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      border-color: var(--v-purpleBrand-base)
      background: var(--v-purplePale-base)

    &:active:not(:disabled)
      box-shadow: 0 2px 4px 0 var(--v-purpleShadow-base) inset

    &:focus-visible:not(:disabled)
      border: 2px solid var(--v-purple100-base)
      color: var(--v-purple100-base)

    // --- DATETIME SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purplePale-base)
      color: var(--v-purpleBrand-base)

  // ========================
  // TYPE: REACTION
  // ========================
  &--reaction
    background: transparent
    color: var(--v-gray70-base)
    border: 1px solid transparent
    transition: color 0.5s ease, border-color 0.5s ease

    &.new-base-button--dark-context
      color: var(--v-white-base)

    &:disabled
      color: var(--v-gray50-base)
      cursor: not-allowed

    &:focus-visible:not(:disabled)
      border-radius: 8px
      border-color: var(--v-tealFocus-base)
      color: var(--v-gray70-base)

    &:hover:not(:disabled)
      color: var(--v-purple100-base)

    &:active:not(:disabled)
      color: var(--v-purpleShadow-base)

  // ========================
  // TYPE: FILTER
  // ========================
  &--filter
    border-radius: 16px
    border: 1px solid var(--v-purpleBrand-base)
    padding: 8px
    font-size: 12px !important
    font-weight: 700
    line-height: 18px
    transition: background 0.5s ease, box-shadow 0.5s ease, color 0.5s ease, border-color 0.5s ease

    // --- FILTER UNSELECTED ---
    &.new-base-button--unselected
      background: transparent
      color: var(--v-purpleBrand-base)

    // --- FILTER SELECTED ---
    &.new-base-button--selected
      background: var(--v-purpleBrand-base)
      color: var(--v-white-base)

    &:hover:not(:disabled)
      background: var(--v-purplePale-base)
      color: var(--v-purpleBrand-base)

    &:focus-visible:not(:disabled)
      border-color: var(--v-tealFocus-base)
      color: var(--v-purpleBrand-base)

    &:disabled
      border-color: var(--v-gray35-base)
      color: var(--v-gray50-base)
      background: transparent

  // ========================
  // TYPE: OPTIONS
  // ========================
  &--options
    border-radius: 8px
    background: var(--v-white-base)
    color: var(--v-black-base)
    border: 1px solid transparent
    transition: background 0.5s ease, box-shadow 0.5s ease, color 0.5s ease, border-color 0.5s ease

    // --- OPTIONS UNSELECTED (DEFAULT) ---
    &.new-base-button--unselected
      background: var(--v-white-base)
      color: var(--v-black-base)

      &:hover:not(:disabled)
        color: var(--v-purpleBrand-base)
        background: var(--v-white-base)

      &:active:not(:disabled)
        color: var(--v-purpleBrand-base)
        background: var(--v-white-base)

      &:focus-visible:not(:disabled)
        border-color: var(--v-tealFocus-base)
        background: var(--v-white-base)
        color: var(--v-black-base)

      &:disabled
        background: var(--v-white-base)
        color: var(--v-black-base)
        cursor: not-allowed
        opacity: 0.6

    // --- OPTIONS SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purple40-base)
      color: var(--v-purpleBrand-base)
      border-color: transparent

      &:hover:not(:disabled)
        color: var(--v-purpleBrand-base)
        background: var(--v-purple40-base)

      &:active:not(:disabled)
        color: var(--v-purpleBrand-base)
        background: var(--v-purple40-base)

      &:focus-visible:not(:disabled)
        border-color: var(--v-tealFocus-base)
        background: var(--v-purple40-base)
        color: var(--v-purpleBrand-base)

      &:disabled
        background: var(--v-purple40-base)
        color: var(--v-purpleBrand-base)
        cursor: not-allowed

  // ========================
  // EXPAND ON SELECT
  // ========================
  &--expand-on-select
    width: 100%
    justify-content: flex-start

  // ========================
  // TYPE: PUBLISH
  // ========================
  &--publish
    border-radius: 100px
    background: var(--v-purpleBrand-base)
    color: var(--v-white-base)
    padding: 16px 22px
    flex-direction: column
    border: 1px solid transparent
    transition: background 0.5s ease, box-shadow 0.5s ease, border-color 0.5s ease

    &:hover:not(:disabled)
      background: var(--v-purpleHover-base)
      box-shadow: 0 4px 3px 0 rgba(3, 201, 186, 0.50)

    &:active:not(:disabled)
      background: var(--v-purpleHover-base)
      box-shadow: 0 4px 4px 0 var(--v-purpleDeep-base) inset

    &:focus-visible:not(:disabled)
      border-color: var(--v-tealFocus-base)
      background: var(--v-purpleBrand-base)

  // ========================
  // TYPE: CHEVRON
  // ========================
  &--chevron
    background: var(--v-purple100-base)
    border-radius: 100px !important
    color: var(--v-white-base)
    max-height: 24px !important
    height: 24px !important
    max-width: 24px !important
    width: 24px !important
    padding: 0 !important

    &.new-base-button--dark-context
      color: var(--v-white-base)

    &:disabled
      background: var(--v-white-base)
      border-radius: 100px !important
      border: 1px solid var(--v-gray35-base)

    &:focus-visible:not(:disabled)
      background: var(--v-white-base)
      border-radius: 100px !important
      border: 1px solid var(--v-gray35-base)

    &:hover:not(:disabled)
      color: var(--v-purple100-base)

    &:active:not(:disabled)
      background: var(--v-white-base)
      border-radius: 100px !important
      border: 1px solid var(--v-gray35-base)
  // ========================
  // TYPE: CLUB
  // ========================
  &--club
    background: var(--v-purple100-base)
    color: var(--v-white-base)
    border: 2px solid transparent

    &:disabled
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    // --- CLUB SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purplePale-base)
      color: var(--v-purple100-base)
      line-height: 20px

  // ========================
  // TYPE: REACTION-MODAL
  // ========================
  &--reactionModal
    background: transparent
    color: var(--v-purple100-base)
    border: 1px solid var(--v-gray50-base) !important
    border-radius: 16px !important

    &:disabled
      color: var(--v-gray50-base)
      cursor: not-allowed
      box-shadow: none

    &:hover:not(:disabled)
      background: var(--v-purplePale-base)
      color: var(--v-purple100-base)

    // --- REACTIONMODAL SELECTED (ATIVO) ---
    &.new-base-button--selected
      background: var(--v-purple40-base)
      color: var(--v-purple100-base)
      line-height: 20px
      border: 1px solid var(--v-gray50-base) !important
      border-radius: 16px !important

      &:hover:not(:disabled)
        background: var(--v-purplePale-base)
        color: var(--v-purple100-base)
</style>
